// Minimal Server-Sent Events parser for fetch() ReadableStreams.
// Handles events split across chunk boundaries, CRLF and LF line endings,
// multi-line data fields, and comment lines (": ping" heartbeats), which are dropped.

export interface SseMessage {
  event: string; // defaults to "message" when the stream omits an event field
  data: string; // data lines joined with "\n"
  id?: string;
}

export interface SseParser {
  /** Feed a decoded chunk of text; returns every message completed by this chunk. */
  feed(chunk: string): SseMessage[];
  /** Flush a trailing message that was not terminated by a blank line. */
  end(): SseMessage[];
}

export function createSseParser(): SseParser {
  let buffer = '';
  let event = '';
  let dataLines: string[] = [];
  let id: string | undefined;
  let hasFields = false;

  const flush = (out: SseMessage[]): void => {
    if (hasFields) {
      const msg: SseMessage = { event: event || 'message', data: dataLines.join('\n') };
      if (id !== undefined) msg.id = id;
      out.push(msg);
    }
    event = '';
    dataLines = [];
    id = undefined;
    hasFields = false;
  };

  const handleLine = (line: string, out: SseMessage[]): void => {
    if (line === '') {
      flush(out);
      return;
    }
    if (line.startsWith(':')) return; // comment / heartbeat
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    switch (field) {
      case 'event':
        event = value;
        hasFields = true;
        break;
      case 'data':
        dataLines.push(value);
        hasFields = true;
        break;
      case 'id':
        id = value;
        hasFields = true;
        break;
      default:
        // "retry" and unknown fields are ignored.
        break;
    }
  };

  return {
    feed(chunk) {
      const out: SseMessage[] = [];
      buffer += chunk;
      let idx: number;
      while ((idx = buffer.indexOf('\n')) !== -1) {
        let line = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 1);
        if (line.endsWith('\r')) line = line.slice(0, -1);
        handleLine(line, out);
      }
      return out;
    },
    end() {
      const out: SseMessage[] = [];
      if (buffer.length > 0) {
        let line = buffer;
        buffer = '';
        if (line.endsWith('\r')) line = line.slice(0, -1);
        handleLine(line, out);
      }
      flush(out);
      return out;
    },
  };
}

/** Iterate SSE messages from a byte stream (for example `response.body`). */
export async function* readSseStream(
  stream: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
): AsyncGenerator<SseMessage> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  const parser = createSseParser();
  const onAbort = (): void => {
    void reader.cancel().catch(() => undefined);
  };
  signal?.addEventListener('abort', onAbort, { once: true });
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      for (const msg of parser.feed(decoder.decode(value, { stream: true }))) yield msg;
    }
    const tail = decoder.decode();
    const rest = tail ? parser.feed(tail) : [];
    for (const msg of rest) yield msg;
    for (const msg of parser.end()) yield msg;
  } finally {
    signal?.removeEventListener('abort', onAbort);
    reader.releaseLock();
  }
}

/** Parse a data payload as JSON, returning undefined when it is not valid JSON. */
export function parseJsonData(data: string): unknown {
  try {
    return JSON.parse(data);
  } catch {
    return undefined;
  }
}
